import { Link } from 'react-router-dom';
import { Phone, Mail, Globe, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
// Logo images
const logoDark = "/Logos/Sayaara Dark Logo.png";
import { CONTACT_INFO } from "../../constants/navigation";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { copyTextToClipboard } from "../../utils/copyToClipboard";

interface FooterProps {
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}

export function Footer({ onOpenTerms, onOpenPrivacy }: FooterProps) {
  const { language, setLanguage, t } = useLanguage();

  const handleCopy = async (text: string) => {
    const ok = await copyTextToClipboard(text);
    if (ok) {
      toast.success(t('common.copiedToClipboard'));
    } else {
      toast.error(t('common.copyFailed'));
    }
  };

  const quickLinks = [
    { name: t('nav.home'), page: 'home', path: '/' },
    { name: t('nav.listings'), page: 'listings', path: '/listings' },
    { name: t('nav.howItWorks'), page: 'how-it-works', path: '/how-it-works' },
    { name: t('nav.about'), page: 'about', path: '/about' }
  ];

  const legalLinks = [
    { name: t('footer.termsConditions'), action: 'terms' },
    { name: t('footer.privacyPolicy'), action: 'privacy' }
  ];

  const handleLegalClick = (action: string) => {
    switch (action) {
      case 'terms':
        onOpenTerms();
        break;
      case 'privacy':
        onOpenPrivacy();
        break;
    }
  };

  return (
    <footer className="bg-[#f91bdc] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <Link to="/">
              <img 
                src={logoDark} 
                alt="Sayaara" 
                className="h-[60px] w-auto mb-4 cursor-pointer hover:scale-105 transition-transform duration-200"
              />
            </Link>
            <p className="text-pink-100 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            
            {/* Language toggle in footer */}
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
                className="border-purple-800 text-purple-900 hover:bg-purple-800 hover:text-white text-sm"
              >
                <Globe size={14} className="mr-2" />
                {t('nav.language')}
              </Button>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.page}>
                  <Link 
                    to={link.path}
                    className="text-pink-100 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleLegalClick(link.action)}
                    className="text-pink-100 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">{t('footer.contactInfo')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3 text-pink-100">
                <Phone size={16} />
                <button
                  type="button"
                  className="text-sm cursor-pointer hover:text-white transition-colors duration-200 text-left"
                  onClick={() => handleCopy(CONTACT_INFO.phone)}
                  title={t('common.copyPhone')}
                >
                  {CONTACT_INFO.phone}
                </button>
              </li>
              <li className="flex items-center space-x-3 text-pink-100">
                <Mail size={16} />
                <button
                  type="button"
                  className="text-sm cursor-pointer hover:text-white transition-colors duration-200 text-left"
                  onClick={() => handleCopy(CONTACT_INFO.email)}
                  title={t('common.copyEmail')}
                >
                  {CONTACT_INFO.email}
                </button>
              </li>
              <li className="flex items-center space-x-3 text-pink-100">
                <Linkedin size={16} />
                <span 
                  className="text-sm cursor-pointer hover:text-white transition-colors duration-200"
                  onClick={() => window.open('https://www.linkedin.com/company/sayaaracanada/', '_blank')}
                >
                  LinkedIn
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-pink-300 mt-8 pt-8 text-center">
          <p className="text-pink-100 text-sm">
            © {new Date().getFullYear()} Sayaara. {t('footer.allRightsReserved')}
          </p>
        </div>
      </div>
    </footer>
  );
}